import React, { useContext, useRef } from 'react';
import styles from './Forms.styles.js';
import useFormsHook from './Forms.hook.js';
import { AppContext } from 'App.context.js';
import { Select, Tooltip, Col, Row, Modal } from 'antd';
import DefaultLayout from 'Components/DefaultLayout/DefaultLayout.jsx';
import SaveButton from 'Components/common/SaveButton/SaveButton.jsx';
import GenerateFormButton from 'Components/common/GenerateFormButton/GenerateFormButton.jsx';
import GuestForms from './GuestForms/GuestForms.jsx';
import DynamicForms from './DynamicForms/DynamicForms.jsx';
import Loading from 'Components/common/Loader/Loading.jsx';
import DotIndicator from 'Components/common/DotIndicator/DotIndicator.jsx';
import Colors from 'constants/Colors.js';
import { InfoCircleOutlined } from '@ant-design/icons';
import constants from 'utils/constants';
import AppButton from 'Components/common/Forms/AppButton/AppButton';
import CopyFormHotelSelector from './CopyFormHotelSelector/CopyFormHotelSelector.jsx';

export default function Forms() {
  const {
    appState,
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);

  const { state, actions } = useFormsHook();
  const formRef = useRef(null);
  const copyFormHotelSelectorRef = useRef(null);

  const languagesMenu = state.languages?.map(lang => (
    <Select.Option key={lang.code} value={lang.code}>
      {lang.label}
    </Select.Option>
  ));

  const formVersions = Object.values(constants.DYNAMIC_FORMS);

  const formVersionSelector = formVersions.map(formVersion => (
    <Select.Option
      key={formVersion}
      value={formVersion}
      disabled={formVersion === constants.DYNAMIC_FORMS.HYBRID}
    >
      {translate(`guestTable.formVersion.${formVersion}`)}
    </Select.Option>
  ));

  return (
    <DefaultLayout
      singleBox
      title={translate('layout.forms')}
      contentHeader={
        <Row justify="space-between" align="top" style={{ marginLeft: 32, marginTop: 16 }}>
          <Col span={6}>
            {state.languages && (
              <Select
                style={{ marginRight: 8 }}
                defaultValue={state.languages[0].code}
                value={state.selectedLanguage}
                onChange={actions.onLanguageSelected}
                disabled={state.isLoading}
              >
                {languagesMenu}
              </Select>
            )}
            {translate('guestTable.languageSelector')}
          </Col>
          <Col span={4}>
            {appState.superUser && state.pmsHasDynamicForms && (
              <div>
                <Select
                  style={{ marginRight: 8 }}
                  defaultValue={formVersions[0]}
                  value={state.selectedformVersion ?? formVersions[0]}
                  onChange={actions.onFormVersionSelected}
                  disabled={state.isLoading}
                >
                  {formVersionSelector}
                </Select>
                {translate('guestTable.formVersionSelector')}
              </div>
            )}
          </Col>
          <Col span={10}>
            {appState.hotelList?.length > 0 && (
              <div style={styles.footer}>
                <SaveButton
                  style={{ marginRight: 10 }}
                  disabled={state.isLoading || !state.userCanEdit}
                  text={translate('save')}
                  action={applyToAllHotels => {
                    formRef.current?.saveClicked(applyToAllHotels);
                  }}
                  showPopConfirm
                  hidden={!state.userCanEdit}
                />
                <AppButton
                  action={() => actions.showCopyFormConfigModal(true)}
                  text={translate('guestTable.copyForm')}
                  secondary
                  disabled={state.isLoading || !state.userCanEdit}
                  hidden={!state.userCanEdit}
                />
                {state.isLoading && (
                  <div style={{ marginLeft: 16 }}>
                    <Loading size="small" />
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
      }
    >
      <div style={styles.headerMapping}>
        {['Cloudbeds', 'Cloudbeds_new', 'Opera', 'Ohip'].includes(state.pmsModuleName) && (
          <div style={styles.textMapping}>
            <DotIndicator color={Colors.yellowPill} />
            <div style={styles.textWithIcon}>
              {translate('guestTable.requiresMapping')}
              <Tooltip
                title={translate('guestTable.requiresMappingDescription')}
                overlayInnerStyle={styles.tooltip}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </div>
          </div>
        )}
        <div style={styles.textMapping}>
          <DotIndicator color={Colors.red} />
          <div style={styles.textWithIcon}>
            {translate('guestTable.unavailableInPMS')}
            <Tooltip
              title={translate('guestTable.unavailableInPMSDescription')}
              overlayInnerStyle={styles.tooltip}
            >
              <InfoCircleOutlined />
            </Tooltip>
          </div>
        </div>
      </div>

      <div style={styles.container}>
        {state.isDynamicForm ? (
          state.form ? (
            <>
              <GenerateFormButton
                translate={translate}
                isLoading={state.isLoading}
                hidden={!state.userCanEdit}
              />
              <DynamicForms
                key={getSelectedHotel()?.key}
                ref={formRef}
                selectedHotel={getSelectedHotel()}
                isLoading={state.isLoading}
                selectedLanguage={state.selectedLanguage}
                form={state.form}
                onSave={actions.saveForm}
                languages={state.languages}
                selectedFieldName={state.selectedFieldName}
                onFieldSelected={actions.saveBaseFieldSelected}
                isDynamicForm={state.isDynamicForm}
              />
            </>
          ) : (
            <div style={styles.dynamicEmpty}>
              <Loading />
            </div>
          )
        ) : (
          <>
            <GenerateFormButton translate={translate} hidden={!state.userCanEdit} />
            <GuestForms
              key={`${getSelectedHotel()?.key}-${state.selectedLanguage}-${
                state.selectedformVersion
              }`}
              ref={formRef}
              isLoading={state.isLoading}
              selectedLanguage={state.selectedLanguage}
              selectedHotelId={getSelectedHotel()?.key}
              languages={state.languages}
              onSave={actions.saveForm}
            />
          </>
        )}
      </div>

      <Modal
        open={state.showCopyFormConfigModal}
        title={translate('guestTable.copyFormTitle')}
        onOk={() =>
          actions.copyFormConfig(
            getSelectedHotel()?.key,
            copyFormHotelSelectorRef.current?.actions?.clearSelectedHotels
          )
        }
        okText={translate('confirm')}
        onCancel={() => actions.showCopyFormConfigModal(false)}
        cancelText={translate('cancel')}
        okButtonProps={{
          disabled: state.copyFormConfigHotelList.length === 0,
        }}
      >
        <Row style={{ marginBottom: 8 }}>
          <Col>
            <span>{translate('guestTable.copyFormDescription')}</span>
          </Col>
        </Row>
        {state.copyErrorMessage && (
          <Row style={{ marginBottom: 8 }}>
            <Col>
              <span style={{ color: 'red' }}>{state.copyErrorMessage}</span>
            </Col>
          </Row>
        )}
        <Row>
          <Col style={{ width: '100%' }}>
            <CopyFormHotelSelector
              ref={copyFormHotelSelectorRef}
              isFormConfigSelector={true}
              enableAllHotels={true}
              autoSelect={true}
              onHotelSelected={actions.setHotelToCopyFormConfig}
            />
          </Col>
        </Row>
      </Modal>
    </DefaultLayout>
  );
}
